import type { IPoint } from '@/modules/Editor/point'
import type { AnnotationClass } from '@/modules/Editor/AnnotationClass'
import { EventEmitter } from 'events'

export interface SkeletonProgressData {
  currentNodeName: string
  currentNodeIndex: number
  totalNodes: number
  placedNodesCount: number
  isComplete: boolean
}

export interface PlacedNode {
  point: IPoint
  name: string
  occluded: boolean
}

export class SkeletonPointByPointManager extends EventEmitter {
  private placedNodes = new Map<string, PlacedNode>()
  private currentNodeIndex = 0
  private nodeOrder: string[] = []
  private obscuredNodes = new Set<string>()
  private totalNodesCount = 0
  private classId: number
  private nextNodeWillBeObscured = false

  constructor(annotationClass: AnnotationClass) {
    super()
    this.classId = annotationClass.id
    this.initializeFromClass(annotationClass)
  }

  private initializeFromClass(annotationClass: AnnotationClass): void {
    if (!annotationClass.skeletonMetaData) {
      throw new Error('Annotation class must have skeleton metadata for point-by-point mode')
    }

    this.nodeOrder = annotationClass.skeletonMetaData.nodes.map((node) => node.name)
    this.totalNodesCount = this.nodeOrder.length
    this.reset()
  }

  placeNode(point: IPoint, isObscured?: boolean): void {
    if (this.isComplete()) return

    const nodeName = this.getCurrentNodeName()
    if (!nodeName) return

    const shouldObscure = isObscured ?? this.nextNodeWillBeObscured

    this.placedNodes.set(nodeName, {
      point,
      name: nodeName,
      occluded: shouldObscure,
    })

    if (shouldObscure) {
      this.obscuredNodes.add(nodeName)
    } else {
      this.obscuredNodes.delete(nodeName)
    }

    this.nextNodeWillBeObscured = false

    this.currentNodeIndex++
    this.emitProgressUpdate()
  }

  removeLastNode(): boolean {
    if (this.currentNodeIndex === 0) {
      return false
    }

    this.currentNodeIndex--
    const nodeName = this.nodeOrder[this.currentNodeIndex]

    this.placedNodes.delete(nodeName)
    this.obscuredNodes.delete(nodeName)

    this.emitProgressUpdate()
    return true
  }

  getCurrentNodeName(): string | null {
    if (this.currentNodeIndex >= this.nodeOrder.length) {
      return null
    }
    return this.nodeOrder[this.currentNodeIndex]
  }

  getProgressData(): SkeletonProgressData {
    const currentNodeName = this.getCurrentNodeName() || ''

    return {
      currentNodeName,
      currentNodeIndex: this.currentNodeIndex,
      totalNodes: this.totalNodesCount,
      placedNodesCount: this.placedNodes.size,
      isComplete: this.isComplete(),
    }
  }

  private emitProgressUpdate(): void {
    this.emit('progress', this.getProgressData())
  }

  isComplete(): boolean {
    return this.currentNodeIndex >= this.totalNodesCount
  }

  getPlacedNodes(): Map<string, PlacedNode> {
    return new Map(this.placedNodes)
  }

  getObscuredNodes(): Set<string> {
    return new Set(this.obscuredNodes)
  }

  getClassId(): number {
    return this.classId
  }

  isNodeObscured(nodeName: string): boolean {
    return this.obscuredNodes.has(nodeName)
  }

  reset(): void {
    this.placedNodes.clear()
    this.obscuredNodes.clear()
    this.currentNodeIndex = 0
    this.nextNodeWillBeObscured = false
    this.emitProgressUpdate()
  }

  toSkeletonAnnotation(): { nodes: Array<{ point: IPoint; name: string; occluded: boolean }> } {
    const nodes = this.nodeOrder.map((nodeName) => {
      const placedNode = this.placedNodes.get(nodeName)
      if (!placedNode) {
        throw new Error(`Node ${nodeName} not placed`)
      }
      return placedNode
    })

    return { nodes }
  }

  toggleNextNodeObscured(): boolean {
    this.nextNodeWillBeObscured = !this.nextNodeWillBeObscured
    return this.nextNodeWillBeObscured
  }

  restoreAllObscuredNodes(): void {
    this.obscuredNodes.clear()
    this.nextNodeWillBeObscured = false

    for (const [nodeName, node] of this.placedNodes) {
      this.placedNodes.set(nodeName, {
        ...node,
        occluded: false,
      })
    }

    this.emitProgressUpdate()
  }

  getNextNodeWillBeObscured(): boolean {
    return this.nextNodeWillBeObscured
  }

  /**
   * Updates the manager with new annotation class data while preserving existing placement state.
   * This is used when the skeleton class metadata is updated during point-by-point placement.
   */
  updateFromClass(annotationClass: AnnotationClass): void {
    if (!annotationClass.skeletonMetaData) {
      throw new Error('Annotation class must have skeleton metadata for point-by-point mode')
    }

    const oldPlacedNodes = new Map(this.placedNodes)
    const oldObscuredNodes = new Set(this.obscuredNodes)

    this.classId = annotationClass.id
    this.nodeOrder = annotationClass.skeletonMetaData.nodes.map((node) => node.name)
    this.totalNodesCount = this.nodeOrder.length
    const newPlacedNodes = new Map<string, PlacedNode>()
    const newObscuredNodes = new Set<string>()

    for (const [nodeName, placedNode] of oldPlacedNodes) {
      if (this.nodeOrder.includes(nodeName)) {
        newPlacedNodes.set(nodeName, placedNode)
        if (oldObscuredNodes.has(nodeName)) {
          newObscuredNodes.add(nodeName)
        }
      }
    }

    this.placedNodes = newPlacedNodes
    this.obscuredNodes = newObscuredNodes

    this.currentNodeIndex = 0
    for (let i = 0; i < this.nodeOrder.length; i++) {
      const nodeName = this.nodeOrder[i]

      if (!this.placedNodes.has(nodeName)) {
        this.currentNodeIndex = i
        break
      }
      this.currentNodeIndex = i + 1
    }

    this.emitProgressUpdate()
  }
}
